<template>
    <v-row class="white-card" align="center" dense>
        <v-col cols="6" class="border-right">
            <div class="acute-text">
                {{ $t('statistics.acute') }}
            </div>
            <div class="acute-text">
                {{ currentAcute }}
            </div>
        </v-col>
        <v-col cols="6" class="p-relative">
            <div class="chronic-text">
                {{ $t('statistics.chronic') }}
            </div>
            <div class="chronic-text">
                {{ currentChronic }}
            </div>
            <div class="ratio-text">{{ $t('statistics.ratio') }} {{ currentRatio }}</div>
        </v-col>

        <v-col cols="12">
            <kimsa-combobox outlined dense solo :items="typeOptions" @change="type = $event" :value="type"
                placeholder-class no-object />
        </v-col>
    </v-row>
</template>

<script>
import KimsaCombobox from '../common/inputs/KimsaCombobox.vue';

export default {
    name: "AcuteChronicChart",
    components: { KimsaCombobox },
    props: {
        docs: {
            type: Array,
            default: null,
        },
        coachId: {
            type: String,
            default: ''
        }
    },
    data(vm) {
        return {
            typeOptions: [
                { name: vm.$t('statistics.wellness'), value: 'wellness' },
                { name: vm.$t('statistics.charge'), value: 'charge' },
                { name: vm.$t('statistics.sets_total'), value: 'sets_total' },
                { name: vm.$t('statistics.sets_strength'), value: 'sets_strength' },
                { name: vm.$t('statistics.sets_speed'), value: 'sets_speed' },
                { name: vm.$t('statistics.sets_resistance'), value: 'sets_resistance' },
                { name: vm.$t('statistics.intensity_strength'), value: 'intensity_strength' },
                { name: vm.$t('statistics.tonnage'), value: 'tonnage' },
                { name: vm.$t('statistics.meters'), value: 'distance' },
                { name: vm.$t('statistics.minutes'), value: 'minutes' },
            ],
            type: 'wellness',
            rpeTable: [
                { name: 10, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 9.5, reps: [97, 93, 89, 86, 84, 82, 80, 77.5] },
                { name: 9, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 8.5, reps: [93, 89, 86, 84, 82, 80, 77.5, 74.5] },
                { name: 8, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 7.5, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 7, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
            rirTable: [
                { name: 0, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 1, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 2, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 3, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 4, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
            acute: {},
            chronic: {},
        }
    },
    computed: {
        currentAcute(vm = this) {
            return vm.acute[vm.type]
        },
        currentChronic(vm = this) {
            return Number(vm.chronic[vm.type] / 4).toFixed(1).replace('.0', '')
        },
        currentRatio(vm = this) {
            return Number(vm.currentAcute / (vm.currentChronic || 1)).toFixed(1).replace('.0', '')
        }
    },
    async mounted() {
        const vm = this
        vm.loadAcuteChronic()
    },
    methods: {
        loadAcuteChronic() {
            const vm = this;

            const endDate = vm.$moment().endOf('week');
            const startDate = vm.$moment(endDate).subtract(4, 'weeks').startOf('week');
            let currentDate = startDate.clone();
            const weekData = { acute: [], chronic1: [], chronic2: [], chronic3: [], chronic4: [] };
            let dayCount = 0;

            // Separar las rutinas por semanas
            while (currentDate.isSameOrBefore(endDate)) {
                const currentRoutine = vm.docs.find(routine => routine.rawDate.full === currentDate.format('YYYY-MM-DD')) || null;
                currentDate.add(1, 'day');
                dayCount++;

                if (currentRoutine) {
                    const week = this.getWeekByDayCount(dayCount);
                    weekData[week].push(currentRoutine);
                }
            }

            vm.acute = this.sumStats(this.loadStats(weekData.acute));
            const chronicTotals = ['chronic1', 'chronic2', 'chronic3', 'chronic4'].map(week => this.sumStats(this.loadStats(weekData[week])));

            vm.chronic = this.sumChronicWeeks(chronicTotals);
        },
        getWeekByDayCount(dayCount) {
            if (dayCount > 28) return 'acute';
            if (dayCount > 21) return 'chronic4';
            if (dayCount > 14) return 'chronic3';
            if (dayCount > 7) return 'chronic2';
            return 'chronic1';
        },
        sumChronicWeeks(chronicWeeks) {
            return chronicWeeks.reduce((totals, weekStats) => {
                Object.keys(weekStats).forEach(key => {
                    totals[key] = (totals[key] || 0) + (Number(weekStats[key]) || 0);
                });
                return totals;
            }, {});
        },
        sumStats(routines) {
            let sumRoutines = routines.reduce((totals, stats) => {
                Object.keys(stats).forEach(key => {
                    const value = stats[key] === 'NaN' ? 0 : Number(stats[key])
                    if (key !== 'date') {
                        totals[key] = (totals[key] || 0) + (value || 0)
                        if (value > 0 && (key === 'wellness' || key === 'intensity_strength')) {
                            totals[`${key}_count`] = (totals[`${key}_count`] || 0) + 1;
                        }
                    }
                });
                return totals;
            }, {});
            return {
                ...sumRoutines,
                wellness: Number(sumRoutines.wellness / (sumRoutines.wellness_count || 1)).toFixed(0),
                intensity_strength: Number(sumRoutines.intensity_strength / (sumRoutines.intensity_strength_count || 1)).toFixed(0),
            }
        },
        loadStats(routines) {
            const vm = this;
            if (routines.length === 0) routines.push({})
            return routines.map(doc => {
                const preInfo = doc?.advance?.preInfo || null
                const wellness = Number((preInfo?.muscles_hurt + preInfo?.sleep_night + preInfo?.stressed + preInfo?.tired) / 4).toFixed(0)
                const postInfo = doc?.advance?.postInfo || null
                const duration = Number(postInfo?.duration || 0)
                const effort = Number(postInfo?.effort || 0)
                const charge = duration * effort
                const minutes = doc.type === 'competence' ? duration : 0

                let sets_total = 0
                let sets_strength = 0
                let sets_speed = 0
                let sets_resistance = 0
                let percentage = 0
                let rpe = 0
                let rir = 0
                let tonnage = 0
                let distance = 0
                let quantity_strong = 0
                doc.blocks?.forEach(block => {
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest') {
                            let sets = Number(exercise.config?.sets?.length || 0)
                            sets_total += sets
                            if (exercise.categories?.[0] === vm.$t('objectives.strong')) sets_strength += sets
                            if (exercise.categories?.[0] === vm.$t('objectives.speed')) sets_speed += sets
                            if (exercise.categories?.[0] === vm.$t('objectives.resistance')) sets_resistance += sets

                            exercise.config?.sets?.forEach((set, i) => {
                                if (exercise.categories?.[0] === vm.$t('objectives.strong')) {
                                    let reps = vm.takeValueFromSet(set, 'reps') || 0
                                    tonnage += (vm.takeValueFromSet(set, 'weight') * reps)
                                    let valuePercentage = vm.takeValueFromSet(set, 'percentage')
                                    percentage += valuePercentage
                                    let valueRpe = vm.takeValueFromSet(set, 'rpe')
                                    rpe += valueRpe ? vm.getValueFromTable(valueRpe, set, 'rpeTable') : 0
                                    let valueRir = vm.takeValueFromSet(set, 'rir')
                                    rir += valueRir ? vm.getValueFromTable(valueRir, set, 'rirTable') : 0
                                    if (valuePercentage > 0 || valueRir > 0 || valueRpe > 0) quantity_strong++
                                } else if (exercise.categories?.[0] === vm.$t('objectives.speed') || exercise.categories?.[0] === vm.$t('objectives.resistance')) {
                                    distance += (vm.takeValueFromSet(set, 'distance') * sets)
                                }
                            })
                        }
                    })
                })

                let intensity_strength = percentage + rpe + rir
                if (quantity_strong > 0) intensity_strength /= quantity_strong

                return {
                    date: doc.rawDate?.full || null,
                    wellness: wellness,
                    charge: charge,
                    sets_total: sets_total,
                    sets_strength: sets_strength,
                    sets_speed: sets_speed,
                    sets_resistance: sets_resistance,
                    intensity_strength: Number(intensity_strength).toFixed(1).replace('.0', ''),
                    tonnage: tonnage,
                    distance: distance,
                    minutes: minutes,
                }
            })
        },
        takeValueFromSet(set, attribute) {
            const vm = this
            let defaultValue = 0
            let value = set.data.find(data => data.name === vm.$t(`exercises.${attribute}`))
            return !!value || value === 0 ? Number(value.value || 0) : defaultValue
        },
        getValueFromTable(value, set, typeTable) {
            const vm = this
            if (typeTable === 'rpeTable') {
                if (value > 10) value = 10
                if (value < 7) value = 7
            }
            if (typeTable === 'rirTable') {
                if (value > 4) value = 4
                if (value < 0) value = 0
            }
            let findValueTable = vm[typeTable]?.find(table => table.name == value) || null
            let reps = set.data.find(data => data.name === vm.$t('exercises.reps')) || null
            let repsValue = reps && (Number(reps.value || 0) - 1) || 0
            if (repsValue > 7) repsValue = 7
            if (repsValue < 0) repsValue = 0
            return findValueTable && repsValue && findValueTable.reps[repsValue] || 0
        },
    },
    watch: {
        docs() {
            this.loadAcuteChronic()
        },
    }
}
</script>

<style scoped>
.border-right {
    border-right: solid 1px #000000;
}

.acute-text {
    color: #000000;
    font-weight: bold;
    text-transform: uppercase;
    text-align: center;
    font-size: 24px;
    font-style: italic;
    margin-top: 16px;
    margin-bottom: 16px;
}

.chronic-text {
    color: #a0d0b5;
    font-weight: bold;
    text-transform: uppercase;
    text-align: center;
    font-size: 24px;
    font-style: italic;
    margin-top: 16px;
    margin-bottom: 16px;
}

.ratio-text {
    position: absolute;
    right: 0;
    bottom: 0;
    text-transform: uppercase;
    font-size: 10px;
}
</style>